import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Spinner, Warning } from '@phosphor-icons/react';
import API_URL from '../utils/apiUrl';
import { trackEvent, EVT } from '../utils/analytics';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const didRun = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invoke — verification tokens are single-use
    if (didRun.current) return;
    didRun.current = true;

    if (!token) {
      setStatus('error');
      setMessage('No verification token found in the link. Please use the link from your email.');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/auth/verify-email`,
          { token },
          { withCredentials: true },
        );
        setStatus('success');
        setUser(data);
        setMessage(data.message || 'Email verified.');
        trackEvent(EVT.EMAIL_VERIFIED, { role: data.role });
        // Refresh AuthContext so the user is now logged in across the app
        await refreshUser();
        // Auto-redirect after 2 seconds. Students -> /welcome (subject router);
        // parents -> /parent/dashboard.
        const destination = data.role === 'parent' ? '/parent/dashboard' : '/welcome';
        setTimeout(() => navigate(destination, { replace: true }), 2000);
      } catch (e) {
        setStatus('error');
        const detail = e?.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : 'This verification link is invalid or has expired.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <div className="neo-card">
          {status === 'verifying' && (
            <div className="py-6" data-testid="verify-status-verifying">
              <Spinner size={44} className="animate-spin text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Confirming your email</h1>
              <p className="text-muted-foreground text-sm">Just a moment...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-6" data-testid="verify-status-success">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle size={40} weight="fill" className="text-emerald-500" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2">Email confirmed.</h1>
              <p className="text-muted-foreground text-sm mb-5">
                {message} Taking you to your first lesson...
              </p>
              <Link
                to={user?.role === 'parent' ? '/parent/dashboard' : '/welcome'}
                className="neo-btn w-full"
                data-testid="verify-go-dashboard-btn"
              >
                Continue
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="py-6" data-testid="verify-status-error">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
                <Warning size={40} weight="fill" className="text-destructive" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2">Verification failed</h1>
              <p className="text-muted-foreground text-sm mb-5">{message}</p>
              <Link to="/login" className="neo-btn w-full" data-testid="verify-back-to-login-btn">
                Back to login
              </Link>
              <p className="text-xs text-muted-foreground mt-4">
                Need a new link? Sign in with your email and password — we&rsquo;ll offer to resend it.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
